import { Grid, Typography, SelectChangeEvent, Dialog, IconButton, Box } from '@mui/material';
import { Formik, validateYupSchema, FormikHelpers, yupToFormErrors } from 'formik';
import MFSelectField from '../../../../lib/formik/SelectField';
import { Applicant, Broker, BrokerList } from '../../../../redux-store/types/api-types';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  grossAnnualMasters,
  occupationDetailsMasters,
  PEPsMasters,
  SourceOfFundMaster,
  USER_ROLES,
  DLCLMasters,
  YesNoMaster,
} from '../../../../utils/constant';
import {
  allowOnlyNumbers,
  applicationComparison,
  applyRoleBasedStatus,
  currencyConversion,
  getApplicantName,
  getOccupationType,
  getSourceOfFund,
  removeSingleQuote,
  clearclIdField,
  cleardpIdField,
  getclIdField,
  getdpIdField,
  isCDSL,
  getCurrentStep,
  navigateToNextStep,
  statusRoleBased,
  isPhysicallyChallenged,
} from '../../../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../../../redux-store/reducers';
import { useEffect, useState } from 'react';
import { getBrokers } from '../../../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { KYCDetailsSchemaForChangeRequest } from '../../../../utils/schema';
import { MFTextField } from '../../../../lib/formik';
import { useSnackbar } from 'notistack';
import MFCheckbox from '../../../../lib/formik/Checkbox';
import { showError } from '../../../../redux-store/actions/auth';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '../../../../lib/formik/DatePicker';
import { SearchableSelect } from '../../../../lib/formik/searchSelectField';
import { Notes, ProceedSaveLater, SubHeading } from '../../../investors/components';
import { updateNctApplication } from '../../../../redux-store/actions/nct';
import UseRadioGroup from '../../../../lib/formik/Radio';

type updatedKyc = Applicant & {
  fundSource: string | null;
  occupationType: string | null;
};

type Values = {
  applicants: Partial<updatedKyc>[];
  saveType: string;
};

const initialValues: Values = {
  applicants: [
    {
      occupationDetails: '',
      occupationType: '',
      grossAnnualIncome: '',
      politicallyExposedPersonStatus: 'Not Applicable',
      ckycNo: '',
      netWorth: '',
      sourceOfFunds: '',
      netWorthDate: '',
      fundSource: '',
      sourceOfFundsLegitimacy: true,
      sourceOfFundsDeclaration: null,
      dlclId: '',
      dpId: '',
      clId: '',
      nameOfBroker: '',
      isPhysicallyDisabled: 'no',
    },
  ],
  saveType: 'save and proceed',
};

export const DematDetails = ({
  values,
  index,
  disabled,
  brokers,
}: {
  values: Values;
  index: number;
  disabled: boolean;
  brokers: Broker[];
}): JSX.Element => {
  return (
    <>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.dlclId`}
          label="Demat Account Details (for allotment of units) *"
          items={Object.keys(DLCLMasters).map((Id) => ({
            key: DLCLMasters[Id],
            value: Id,
          }))}
          disabled={disabled}
        />
      </Grid>
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.dpId`}
            label={`${isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'} *`}
            placeholder={`Enter ${
              isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'
            }`}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            disabled={disabled}
          />
        </Grid>
      )}
      {getclIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.clId`}
            label="CL ID *"
            placeholder="Enter CL ID"
            disabled={disabled}
          />
        </Grid>
      )}
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <SearchableSelect
            name={`applicants.${index}.nameOfBroker`}
            label="Name of Broker/Depository Participant (DP) *"
            items={brokers?.map((broker: Broker) => ({
              key: broker.value,
              value: broker.key,
            }))}
            searchFieldPlaceholder={'Search Broker/Depository Participant'}
          />
        </Grid>
      )}
    </>
  );
};

const KYCDetails = ({
  applicant,
  index,
  values,
  setValues,
  brokers,
}: {
  applicant: string;
  index: number;
  values: Values;
  setValues: FormikHelpers<Values>['setValues'];
  brokers: Broker[];
}): JSX.Element => {
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [open, setOpen] = useState(false);

  const handleOpenf = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const isFieldDisabled = [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role);
  return (
    <>
      <SubHeading>Additional KYC Details of {applicant} Applicant</SubHeading>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.occupationType`}
          label="Occupation Details *"
          items={occupationDetailsMasters.map((occupation) => ({
            key: occupation,
            value: occupation,
          }))}
          onChange={(e) => {
            setValues({
              ...values,
              applicants: values.applicants.map((_applicant, _index) => {
                if (_index === index) {
                  return {
                    ..._applicant,
                    occupationType: e.target.value,
                    occupationDetails: '',
                  };
                }
                return _applicant;
              }) as Partial<updatedKyc>[],
            });
          }}
        />
      </Grid>
      {values.applicants.map((applicant, idx: number) => {
        if (applicant.occupationType === 'OTHERS' && idx === index) {
          return (
            <Grid item xs={12} sm={6}>
              <MFTextField
                name={`applicants.${index}.occupationDetails`}
                label="Please Specify Occupation Type *"
                placeholder={`Enter Occupation Type`}
                onChange={(e) => {
                  const occupationDetails = e.target.value.replace(/\s+/g, ' ');
                  setValues({
                    ...values,
                    applicants: values.applicants.map((_applicant, _index) => {
                      if (_index === index) {
                        return {
                          ..._applicant,
                          occupationDetails: occupationDetails,
                        };
                      }
                      return _applicant;
                    }) as Partial<updatedKyc>[],
                  });
                }}
              />
            </Grid>
          );
        }
      })}
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.grossAnnualIncome`}
          label="Gross Annual Income *"
          items={grossAnnualMasters.map((grossIncome) => ({
            key: grossIncome,
            value: grossIncome,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.netWorth`}
          label="Net Worth *"
          placeholder="Enter Net Worth"
        />
        {values.applicants[index].netWorth && !isNaN(Number(values.applicants[index].netWorth)) && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.applicants[index].netWorth)}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={'Net worth as on *'}
          inputLabelStyles={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
          }}
          placeholder={'Enter Net worth as on'}
          name={`applicants.${index}.netWorthDate`}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.politicallyExposedPersonStatus`}
          label="Politically Exposed Person (PEP) Status *"
          items={PEPsMasters.map((pep) => ({ key: pep.key, value: pep.value }))}
        />
        <Typography
          component="span"
          sx={{ position: 'relative', left: { lg: 286, xs: 286 }, bottom: { xs: 85, md: 95 } }}>
          <HelpOutlineIcon
            color="info"
            fontSize="small"
            onClick={handleOpenf}
            sx={{ cursor: 'pointer', pointerEvents: 'all' }}
          />
        </Typography>
        <Dialog
          onClose={handleClose}
          open={open}
          sx={{
            '.MuiPaper-root ': {
              maxWidth: 800,
              p: { xs: 2, sm: '15px 20px 45px' },
              borderRadius: '10px',
              height: 'unset',
              overflowY: 'auto',
            },
          }}>
          <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
          <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
            <HelpOutlineIcon color="info" fontSize="large" />
          </Box>
          <Box
            sx={{
              width: { xs: '100%', sm: '80%' },
              mx: 'auto',
              '.MuiTypography-root': {
                fontSize: 20,
                fontWeight: 500,
                textAlign: 'center',
              },
            }}>
            <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>
              {
                'Politically Exposed Persons (PEP) are defined as individuals are, or have been, entrusted with prominent public functions and their family members and close associates in a domestic or foreign country, e.g., Heads of States or of Governments, senior politicians, senior Government/judicial/ military officers, senior executives of state-owned corporations, important political party officials, etc.'
              }
            </Typography>
          </Box>
        </Dialog>
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.ckycNo`}
          label="CKYC Number"
          placeholder={'Enter CKYC number'}
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
          disabled={isFieldDisabled || false}
        />
      </Grid>
      <DematDetails
        values={values}
        index={index}
        disabled={isFieldDisabled || false}
        brokers={brokers as Broker[]}
      />
      <Grid item xs={12} sm={12}>
        <MFCheckbox
          name={`applicants.${index}.sourceOfFundsLegitimacy`}
          label={
            'I/we hereby confirm that the funds injected/invested have been legitimately and legally obtained/earned and the derivation and use of the funds do not contravene applicable anti-money laundering and terrorist financing legislation.'
          }
          onChange={({ target: { checked } }) => {
            setValues({
              ...values,
              applicants: values.applicants.map((detail: any, idx: number) => {
                if (index === idx) {
                  return {
                    ...detail,
                    sourceOfFundsLegitimacy: checked,
                    sourceOfFunds: '',
                    fundSource: '',
                  };
                }
                return detail;
              }),
            });
          }}
        />
      </Grid>
      {values.applicants[index].sourceOfFundsLegitimacy && (
        <Grid item xs={12} sm={12}>
          <MFSelectField
            name={`applicants.${index}.fundSource`}
            label="Source of Funds *"
            items={Object.keys(SourceOfFundMaster).map((source) => ({
              key: SourceOfFundMaster[source as string],
              value: source,
            }))}
            onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
              setValues({
                ...values,
                applicants: values.applicants.map((detail: any, idx: number) => {
                  if (index === idx) {
                    return {
                      ...detail,
                      fundSource: value,
                      sourceOfFunds: '',
                    };
                  }
                  return detail;
                }),
              });
            }}
          />
          {values.applicants[index].fundSource === 'others' && (
            <MFTextField
              name={`applicants.${index}.sourceOfFunds`}
              label="Please Specify Source of Funds *"
              placeholder={`Enter Source of Funds`}
            />
          )}
        </Grid>
      )}
      <Grid item xs={12} sm={12}>
        <MFCheckbox
          name={`applicants.${index}.sourceOfFundsDeclaration`}
          label={
            'I/we hereby confirm that the aforementioned information is true and correct. I/we will also provide documentary evidence of the source of funds if / when required.'
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} mt={2}>
        <UseRadioGroup
          formLabel="Is Investor a Person with Disability *"
          name={`applicants.${index}.isPhysicallyDisabled`}
          items={Object.keys(YesNoMaster).map((ele) => ({
            label: YesNoMaster[ele],
            value: ele,
          }))}
        />
      </Grid>
      {isPhysicallyChallenged(values.applicants[index].isPhysicallyDisabled) && (
        <Grid item xs={12}>
          <Notes
            displayContent="Our authorised representative will contact you for any assistance
            you may need."
          />
        </Grid>
      )}
    </>
  );
};

export default function AdditionalKYCDetailsNCT(): JSX.Element {
  const [kycDetails, setkycDetails] = useState(initialValues);
  const { application } = useSelector((store: RootStateType) => store.nctApplication);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [brokerList, setBrokerList] = useState<BrokerList>();
  const location = useLocation();
  const path = location.pathname;
  console.log('path', path);

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getBrokers())) as unknown as BrokerList;
        setBrokerList(response as BrokerList);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  useEffect(() => {
    const { applicants: exisitingApplicants = [] } = application || {};
    setkycDetails({
      ...kycDetails,
      applicants: exisitingApplicants.map((applicant) => ({
        occupationType: getOccupationType(applicant.occupationDetails)
          ? applicant.occupationDetails
          : 'OTHERS',
        occupationDetails:
          applicant.occupationDetails === 'OTHERS'
            ? ''
            : getOccupationType(applicant.occupationDetails)
            ? ''
            : applicant.occupationDetails,
        grossAnnualIncome: applicant.grossAnnualIncome || '',
        politicallyExposedPersonStatus: applicant.politicallyExposedPersonStatus
          ? applicant.politicallyExposedPersonStatus
          : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)
          ? ''
          : 'Not Applicable',
        ckycNo: applicant.ckycNo || '',
        sourceOfFunds: applicant.sourceOfFunds || '',
        sourceOfFundsLegitimacy: applicant.sourceOfFundsLegitimacy || true,
        sourceOfFundsDeclaration: applicant.sourceOfFundsDeclaration || false,
        fundSource: getSourceOfFund(applicant.sourceOfFunds)
          ? applicant.sourceOfFunds
          : !applicant.sourceOfFunds
          ? ''
          : 'others',
        netWorth: applicant.netWorth || '',
        netWorthDate: applicant.netWorthDate || '',
        dlclId: applicant.dlclId ? applicant.dlclId : '',
        dpId: applicant.dpId || '',
        clId: applicant.clId || '',
        nameOfBroker: applicant.nameOfBroker || '',
        isPhysicallyDisabled: applicant.isPhysicallyDisabled || 'no',
      })),
    });
  }, [application]);

  const handleSubmit = async (values: Values) => {
    try {
      const { applicants, saveType } = values;
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        status,
        hasPOA,
        applicationNumber,
      } = application || {};

      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        const { fundSource, occupationType, ...rest } = applicants[index];
        if (
          applicants[index].sourceOfFundsLegitimacy === false ||
          applicants[index].sourceOfFundsDeclaration === false
        ) {
          throw 'Declaration is required';
        }
        return {
          ...applicant,
          ...rest,
          sourceOfFunds: getSourceOfFund(applicants[index].fundSource)
            ? applicants[index].fundSource
            : applicants[index].sourceOfFunds,
          occupationDetails: getOccupationType(applicants[index].occupationDetails)
            ? occupationType
            : removeSingleQuote(applicants[index].occupationDetails),
          clId: clearclIdField(applicants[index].dlclId || '') ? '' : applicants[index].clId,
          dpId: cleardpIdField(applicants[index].dlclId || '')
            ? ''
            : applicants[index]?.dpId?.toUpperCase(),
        };
      });
      const checkApplication = applicationComparison(application, {
        ...application,
        applicants: updatedApplicants,
        // currentStep: !!currentStep && currentStep > 2 ? currentStep : Number(currentStep) + 1,
        currentStep:
          !!currentStep &&
          currentStep > getCurrentStep(application?.applicationType || '', path) - 1
            ? currentStep
            : getCurrentStep(application?.applicationType || '', path),
      });
      const isSaveLater = saveType !== 'save and proceed';
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateNctApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              status: statusRoleBased(role, application),
              // currentStep: 3,
              currentStep: getCurrentStep(application?.applicationType || '', path),
              //!!currentStep && currentStep > 3 ? currentStep : Number(currentStep) + 1,
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || ''
          ),
          {
            id,
            applicant1ReferenceId,
          }
        );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || ''
          ),
          {
            id,
            applicant1ReferenceId,
          }
        );
      }
    } catch (e) {
      setLoading(false);
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    }
  };
  return (
    <Formik
      initialValues={kycDetails}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            KYCDetailsSchemaForChangeRequest(
              brokerList?.broker_list as Broker[],
              application?.createdAt || ''
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues, errors }) => (
        <Grid
          container
          rowSpacing={1}
          // columnSpacing={5}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          {values.applicants.map((_, index) => (
            <KYCDetails
              applicant={getApplicantName(index + 1)}
              key={index}
              index={index}
              values={values}
              setValues={setValues}
              brokers={brokerList?.broker_list as Broker[]}
            />
          ))}
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
